import { useState, type FormEvent } from 'react';
import { sendPasswordReset, authErrorMessage } from '@/lib/auth';

interface PasswordResetFormProps {
  onSwitchMode: (mode: 'signin') => void;
}

export default function PasswordResetForm({ onSwitchMode }: PasswordResetFormProps) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await sendPasswordReset(email.trim());
      setSent(true);
    } catch (err) {
      setError(authErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="auth-form">
        <div className="auth-success">
          <strong>Check your inbox.</strong>
          <p>
            If an account exists for <span style={{ fontFamily: 'var(--mono)' }}>{email}</span>,
            we just sent a reset link. It may take a minute to arrive.
          </p>
        </div>
        <button type="button" className="btn block" onClick={() => onSwitchMode('signin')}>
          ← Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 6 }}>
        Enter the email on your account. We'll send a link to reset your password.
      </p>
      <label className="auth-field">
        <span className="auth-label">Email</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={busy}
        />
      </label>

      {error && <div className="auth-error">{error}</div>}

      <button type="submit" className="btn block" disabled={busy}>
        {busy ? 'Sending…' : 'Send Reset Link →'}
      </button>

      <div className="auth-links">
        <button type="button" className="auth-link" onClick={() => onSwitchMode('signin')}>
          ← Back to sign in
        </button>
      </div>
    </form>
  );
}
